import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { FormattedMessage, useIntl } from "react-intl";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setDesignItem } from "../../services/cdiDetailSlice";
import {
	useBookmarkDesignItemMutation,
	useFetchDesignItemQuery,
	useFetchDetailSceneQuery,
	useUnbookmarkDesignItemMutation,
} from "../../services/clApiSlice";
import { useAddNewLogMutation } from "../../services/seqApiSlice";
import { selectSeqData } from "../../services/seqDataSlice";
import { UserInformations } from "../../types/User";
import setNewLog from "../../functions/setNewLog/setNewLog";
import Message from "../common/Message";
import Loader from "../common/Loader";
import CanvasRenderer from "../common/CanvasRenderer";
import ScenePreviewOverlay from "../common/ScenePreviewOverlay";
import {
	ConDesignItemLite,
	ConDesignItemWithPicture,
	ManufacturingTypes,
} from "../../types/ConDesignItem";
import { capitalize } from "../../functions/capitalize/capitalize";
import { useFetchSceneJsonQuery } from "../../services/sceneSlice";
import { CrossIcon, MaximizeIcon } from "../../assets/icons";
import { DESIGN_CODES } from "../../localization/designCodes";
import CDIDetailLayout from "./CDIDetailLayout";
import OpenInDesktopButton from "../common/OpenInDesktopButton";
import { ReactComponent as BookmarkIcon } from "../../assets/bookmark.svg";
import { ReactComponent as BookmarkFilledIcon } from "../../assets/bookmark-filled.svg";
import { ReactComponent as BookmarkCount } from "../../assets/bookmarkCount.svg";
import { ReactComponent as DownloadCount } from "../../assets/downloadCount.svg";
import { toast } from "react-hot-toast";
import { FeatureHint } from "../common/FeatureHint";
import { BookmarkErrorResponse } from "../../types/BookmarkResponse";

interface CDIDetailProps {
	userInformations: UserInformations | undefined;
	setExpiredTokenItem: (item: ConDesignItemLite) => void;
	redirectUrl: string;
}

const getManufacturingTypesEnumString = (value: number): string => {
	const separatorOfResult = "&";
	const enumStrings: string[] = [];
	
	for (const key in ManufacturingTypes) {
		if (typeof ManufacturingTypes[key] === "number") {
			const enumValue = Number(ManufacturingTypes[key]);
			if ((value & enumValue) !== 0) {
				enumStrings.push(capitalize(key));
			}
		}
	}
	
	return enumStrings.join(` ${separatorOfResult} `);
};

const CDIDetail = ({ userInformations, setExpiredTokenItem, redirectUrl }: CDIDetailProps) => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useAppDispatch();
	const { id, itemId } = useParams();
	const [addNewLog] = useAddNewLogMutation();
	const seqData = useAppSelector(selectSeqData);
	const intl = useIntl();
	const [projectDownloadsExceeded, setProjectDownloadsExceeded] = useState(false);
	const [isSceneMaximized, setIsSceneMaximized] = useState(false);
	
	const [bookmarkItem, { isLoading: isBookmarking }] = useBookmarkDesignItemMutation();
	
	const [unbookmarkItem, { isLoading: isUnbookmarking }] = useUnbookmarkDesignItemMutation();
	
	const {
		data: sceneUrlData,
		error: sceneUrlError,
		isLoading: isLoadingSceneUrl,
		isFetching: isFetchingSceneUrl,
	} = useFetchDetailSceneQuery(itemId ? itemId : skipToken);
	
	const {
		data: sceneJsonData,
		error: sceneJsonError,
		isLoading: isLoadingSceneJson,
		isFetching: isFetchingSceneJson,
		isSuccess: isSuccessSceneJson,
	} = useFetchSceneJsonQuery(
		sceneUrlData && sceneUrlData.webScene3dDataUrl ? sceneUrlData?.webScene3dDataUrl : skipToken,
	);
	
	const {
		data: designItemData,
		error: designItemError,
		isLoading: isLoadingDesignItem,
		isSuccess: isSuccessDesignItem,
	} = useFetchDesignItemQuery(itemId ? itemId : skipToken);
	
	const getRedirectUrl = () => {
		const urlParams = new URLSearchParams(location.search);
		const backRedirectUri = urlParams.get("redirectUri");
		
		if (backRedirectUri) {
			return `/account/bookmarks?redirectUri=${encodeURIComponent(backRedirectUri)}`;
		}
		return redirectUrl; // fallback to the prop value
	};
	
	useEffect(() => {
		if (designItemData) {
			dispatch(setDesignItem(designItemData));
		}
		setProjectDownloadsExceeded(false);
		setIsSceneMaximized(false);
	}, [dispatch, designItemData]);
	
	useEffect(() => {
		const expiredTokenItemJson = localStorage.getItem("detailDesignItem");
		if (
			sceneUrlError &&
			"status" in sceneUrlError &&
			sceneUrlError.status === 401 &&
			expiredTokenItemJson
		) {
			const conDesignItem = JSON.parse(expiredTokenItemJson) as ConDesignItemLite;
			setExpiredTokenItem(conDesignItem);
			
			navigate(redirectUrl);
		}
	}, [sceneUrlError, navigate, id, setExpiredTokenItem, redirectUrl]);
	
	useEffect(() => {
		if (
			seqData &&
			designItemData &&
			userInformations &&
			userInformations.userIds.licenseId &&
			userInformations.userIds.userId &&
			userInformations.userIds.correlationId
		) {
			const timestamp = new Date();
			const logObject = {
				"logString": setNewLog(
					timestamp,
					"Design item with ID {ConBrowser_DesignItemId} for the user with ID {UserAccountId} and design set ID {ConBrowser_DesignSetId} was found",
					userInformations.userIds.correlationId,
					userInformations.userIds.licenseId,
					userInformations.userIds.userId,
					undefined,
					designItemData.designItem.conDesignSetId,
					designItemData.designItem.conDesignItemId,
				),
				"seqEndpoint": seqData.seqEndpoint,
				"seqApiKey": seqData.seqKey,
			};
			addNewLog(logObject);
		}
	}, [addNewLog, seqData, userInformations, designItemData]);
	
	const version = Number(designItemData?.designItem.version);
	
	const onCloseDetailPage = () => {
		navigate(getRedirectUrl());
	};
	
	const designCode = DESIGN_CODES.find((code) => code.id === designItemData?.designItem.designCode);
	
	const getConnectors = (designData: ConDesignItemWithPicture) => {
		const enumValue = designData.designItem?.designData?.manufacture?.manufacturingType;
		return enumValue === null
			? intl.formatMessage({ id: "NoConnector", defaultMessage: "No connector" })
			: getManufacturingTypesEnumString(enumValue);
	};
	
	// Handle bookmark toggle
	const handleBookmarkToggle = async () => {
		if (!designItemData?.designItem.conDesignItemId) return;
		
		// Choose the correct mutation based on current bookmark status
		const mutation = designItemData.isBookmarked ? unbookmarkItem : bookmarkItem;
		const isBookmarked = designItemData.isBookmarked;
		
		window.dataLayer.push({
			"event": isBookmarked ? "cl_detail_bookmark_remove_clicked" : "cl_detail_bookmark_add_clicked",
		});
		
		const bookmarkPromise = mutation(designItemData.designItem.conDesignItemId).unwrap();
		
		toast.promise(bookmarkPromise, {
			loading: <FormattedMessage id="Bookmark_loading" defaultMessage="Updating bookmark..." />,
			success: (result) => {
				if (result.success) {
					return (
						<FormattedMessage
							id={isBookmarked ? "Unbookmark_success" : "Bookmark_success"}
							defaultMessage={
								isBookmarked ? "Item unbookmarked successfully" : "Item bookmarked successfully"
							}
						/>
					);
				} else {
					
					const error = new Error(result.message || "Operation failed");
					error.message = result.message || "Operation failed";
					throw error;
				}
			},
			error: (error) => {
				console.error("Error toggling bookmark:", error);
				
				const errorData = (error as any).data as BookmarkErrorResponse | undefined;
				
				if (errorData?.error === "BookmarkLimitExceeded") {
					return (
						<FormattedMessage
							id="Bookmark_limit_error"
							defaultMessage="Maximum number of bookmarks reached. Please remove some bookmarks before adding new ones."
						/>
					);
				}
				
				return (
					<FormattedMessage
						id="Bookmark_error"
						defaultMessage="Failed to update bookmark. Please try again."
					/>
				);
			},
		});
	};
	
	function formatNumber(num: number): string {
		if (num >= 1_000_000_000) {
			return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
		}
		if (num >= 1_000_000) {
			return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
		}
		if (num >= 1_000) {
			return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
		}
		return num.toString();
	}
	
	if (
		isLoadingDesignItem ||
		isLoadingSceneJson ||
		isFetchingSceneJson ||
		isLoadingSceneUrl ||
		isFetchingSceneUrl
	) {
		return (
			<CDIDetailLayout onClose={onCloseDetailPage}>
				<Loader />
			</CDIDetailLayout>
		);
	}
	
	if (designItemError) {
		return (
			<CDIDetailLayout onClose={onCloseDetailPage}>
				<Message
					detailPage
					message={
						<FormattedMessage
							id="DesignItem_NotFound_TryAgainLater"
							defaultMessage="Design item was not found, try again later"
						/>
					}
				/>
			</CDIDetailLayout>
		);
	}
	
	if (sceneJsonError || sceneUrlError) {
		return (
			<CDIDetailLayout onClose={onCloseDetailPage}>
				<Message
					detailPage
					message={
						<FormattedMessage
							id="SceneData_NotFound"
							defaultMessage="Scene data was not found, try again later"
						/>
					}
				/>
			</CDIDetailLayout>
		);
	}
	
	if (projectDownloadsExceeded) {
		return (
			<CDIDetailLayout onClose={onCloseDetailPage}>
				<Message
					detailPage
					message={
						<FormattedMessage
							id="DesignItem_Downloads_Exceeded"
							defaultMessage="You have reached your project download limit. Please try again tomorrow."
						/>
					}
				/>
			</CDIDetailLayout>
		);
	}
	
	return (
		<div className="CDIDetail">
			<div className="close-icon" onClick={onCloseDetailPage}>
				<CrossIcon />
			</div>

			{isSuccessDesignItem && designItemData && sceneJsonData && isSuccessSceneJson ? (
				<>
					<div
						className="maximize-icon"
						onClick={() => setIsSceneMaximized(true)}
						role="button"
						tabIndex={0}
						title={intl.formatMessage({
							id: "MaximizePreview",
							defaultMessage: "Enlarge 3D model",
						})}
					>
						<MaximizeIcon />
					</div>

					<div className="CDIDetail-scene-wrapper">
						<CanvasRenderer
							key={designItemData.designItem.conDesignItemId}
							scene={sceneJsonData}
							detailPage
						/>
					</div>

					{isSceneMaximized && (
						<ScenePreviewOverlay
							scene={sceneJsonData}
							sceneKey={`${designItemData.designItem.conDesignItemId}-fullscreen`}
							onClose={() => setIsSceneMaximized(false)}
						/>
					)}

					<div className="CDIDetail-itemStats-group">
						<div className="CDIDetail-itemStats-group-item">
							<BookmarkCount />
							{formatNumber(designItemData.designItem.bookmarkCount || 0)}
						</div>
						<div className="CDIDetail-itemStats-group-item">
							<DownloadCount />
							{formatNumber(designItemData.designItem.downloadCount || 0)}{" "}
							{intl.formatMessage({
								id: "Users",
								defaultMessage: "users",
							})}
						</div>
					</div>
					
					<div className="CDIDetail-itemStats">
						<div className="CDIDetail-properties-top">
							<h2 className="CDIDetail-heading">
								<FormattedMessage id="Properties" defaultMessage="Properties" />
							</h2>
						</div>
						<span className="CDIDetail-description">
							<span className="CDIDetail-description-name">
								{intl.formatMessage({ id: "DesignCode", defaultMessage: "Design code" })}
							</span>
							<span className="CDIDetail-description-value">
								{designItemData.designItem.designCode && designCode
									? intl.formatMessage({
										id: designItemData.designItem.designCode,
										defaultMessage: designCode.defaultMessage,
									})
									: ""}
							</span>
						</span>
						<span className="CDIDetail-description">
							<span className="CDIDetail-description-name">
								{intl.formatMessage({ id: "Connectors", defaultMessage: "Connectors" })}
							</span>
							<div className="CDIDetail-description-value">{getConnectors(designItemData)}</div>
						</span>
						<div className="CDIDetail-actions">
							<OpenInDesktopButton
								version={version}
								userInformations={userInformations}
								designItem={designItemData}
								setProjectDownloadsExceeded={setProjectDownloadsExceeded}
							/>
							
							<FeatureHint
								id="bookmark-hint"
								side="bottom"
								align="end"
								trigger={
									<button
										className="CDIDetail-actions-bookmark"
										onClick={handleBookmarkToggle}
										disabled={isBookmarking || isUnbookmarking}
									>
										{designItemData.isBookmarked ? <BookmarkFilledIcon /> : <BookmarkIcon />}
									</button>
								}
								title="Boomark connections"
								description={
									<FormattedMessage
										id="BookmarkTooltipMessage"
										defaultMessage="Click the bookmark icon to save a connection. View saved items: Profile > My bookmarks."
									/>
								}
							/>
						</div>
						
						<div className="CDIDetail-properties-download">
							<span className="CDIDetail-properties-download-text">
								{intl.formatMessage({
									id: "DontHaveDesktop",
									defaultMessage: "Don't have the desktop application installed yet?",
								})}
							</span>
						</div>
						<div className="CDIDetail-properties-download-now">
							<a
								href="https://www.ideastatica.com/product-downloads"
								target="_blank"
								rel="noreferrer"
							>
								{intl.formatMessage({
									id: "DownloadIdeaNow",
									defaultMessage: "Download now!",
								})}
							</a>
						</div>
					</div>
				</>
			) : (
				""
			)}
		</div>
	);
};

export default CDIDetail;
